import { ReactElement, SVGProps, useId, useMemo } from 'react';

import { useChartContext } from '../../ChartProvider';

export type ColorTransition = 'smooth' | 'sharp';

type GradientStopProps = SVGProps<SVGStopElement> & {
  transition?: ColorTransition;
};

export const useGradient = <T,>(
  getStylesByPoint:
    | ((dataPoint: T, index: number) => GradientStopProps)
    | undefined,
  defaultTransition: ColorTransition
) => {
  const id = useId();
  const gradientId = `${id}_gradient`;

  const { data } = useChartContext<T>();

  return useMemo(() => {
    if (!getStylesByPoint || data.length < 2)
      return {
        gradientStops: [],
        gradientId,
      };

    const calculateOffset = (index: number) =>
      (index / (data.length - 1)) * 100;

    const stops: ReactElement[] = [];
    let prevProps: GradientStopProps | undefined;

    data.forEach((point, index) => {
      const { transition, ...stopProps } = getStylesByPoint(point, index);
      const effectiveTransition = transition ?? defaultTransition;
      const offset = `${calculateOffset(index)}%`;

      if (effectiveTransition === 'sharp' && index > 0 && prevProps) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { transition, ...rest } = prevProps;
        stops.push(<stop {...rest} offset={offset} key={`${index}-prev`} />);
      }

      stops.push(<stop {...stopProps} offset={offset} key={index} />);

      prevProps = { transition, ...stopProps };
    });

    return {
      gradientStops: stops,
      gradientId,
    };
  }, [data, gradientId, getStylesByPoint, defaultTransition]);
};
